import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '../routing';
import { analysisApi, formatCurrency, formatCurrencyWhole } from '../utils/api';
import { periodLabel, PRESETS_ANALYTIC, activePresetKey, withPeriodParams } from '../utils/period';
import { topProductKey, TOP_PRODUCTS_REPORT_PATH } from '../utils/topProducts';
import { useIsMobile } from '../utils/useIsMobile';
import Icon from './Icon';
import EmptyState from './EmptyState';
import InlineLoadError from './InlineLoadError';
import { RowChangeChip } from './TopProductSheet';

// "10 המוצרים המובילים" (owner, 23/10/2026) — the ten products the restaurant spent the most on in
// the picked period, beside רכש לפי ספקים and רכש לפי סיווג פריטים on /analysis.
//
// A PLAIN, READ-ONLY TABLE (owner + product-design review, 21/10/2026). The
// owner's goal for the card is four facts per product — what it costs now, how
// that moved, how much was bought, and from whom — and the earlier versions
// (min–max bars, a header strip, chips beside prices, tappable rows opening
// orders) read as crowded. So:
//   phone    מוצר · ספק │ מחיר נוכחי (% under it) │ סה״כ רכש
//   desktop  מוצר · ספק │ מחיר נוכחי + % │ סה״כ רכש — one table of ten rows,
//            the name column only as wide as the longest name, so the price
//            sits by the name (owner, 21/10: a separate ספק column left the
//            prices far from the names)
// One header row names the columns once; rows are not clickable. Everything
// else — orders, min–max, quantity, search, Excel — lives in the full report
// (/analysis/reports/top-products), reached from the title, ⋮ and the footer.
// "סה״כ רכש" is in shekels, not quantity: products are bought in different
// units, so quantities cannot be compared down the list.
//
// Ranked by the total. The amount is spend after invoice discounts and credits
// — the same money as סה״כ רכש — and lines marked לא מוצר or שירות are out.
// See lib/topProducts.js. Gated by top_products_enabled (AnalysisPage renders
// it only then); every role sees it.

// The ⋮ menu's "למה זה חשוב": what an owner can DO with the card, in three
// short lines — the card exists to start a conversation with a supplier.
export function TopProductsWhyItMatters() {
  return (
    <div className="top-products-how">
      <div className="top-products-how-title">למה זה חשוב</div>
      <p className="top-products-why-lead">על המוצרים האלה הולך הכי הרבה כסף, ולכן כל הורדת מחיר בהם חוסכת הכי הרבה.</p>
      <ul className="top-products-why">
        <li>המחיר עלה (אדום)? בקשו מהספק את המחיר הקודם.</li>
        <li>בדוח המלא רואים את המחיר הנמוך בתקופה. הראו לספק שכבר קיבלתם מחיר נמוך יותר.</li>
        <li>השוו מחיר מול ספק אחר, או בקשו הנחה על כמות.</li>
      </ul>
    </div>
  );
}

// The ⋮ menu's "איך מחושב": one short line per column of the card.
export function TopProductsHowCalculated() {
  const items = [
    ['הסדר', 'לפי סה״כ הרכש של המוצר בתקופה, מהגבוה לנמוך.'],
    ['מחיר נוכחי', 'המחיר במסמך האחרון בתקופה, כפי שבחשבונית.'],
    ['% שינוי', 'לעומת הקנייה הראשונה בתקופה. אדום – עלה, ירוק – ירד. בלי אחוז – נקנה פעם אחת בתקופה.'],
    ['סה״כ רכש', 'כמה שולם על המוצר בתקופה, ללא מע״מ, אחרי הנחות וזיכויים.'],
  ];
  return (
    <div className="top-products-how">
      <div className="top-products-how-title">איך מחושב</div>
      <dl>
        {items.map(([term, text]) => (
          <div key={term}><dt>{term}</dt><dd>{text}</dd></div>
        ))}
      </dl>
      <p>מוצר נספר בנפרד אצל כל ספק. לא כולל טיוטות ושורות שסומנו ״לא מוצר״ או ״שירות״.</p>
    </div>
  );
}

const price = (r) => (r.current_price == null ? '—' : formatCurrency(r.current_price));

export default function TopProductsCard({ params, filterActive = false }) {
  const navigate = useNavigate();
  // 640, the density breakpoint: below it the % sits under the price, above it
  // beside the price.
  const phone = useIsMobile(640);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [titleHover, setTitleHover] = useState(false);

  const key = JSON.stringify(params);

  // A generation counter, not a per-call `alive` flag: fetchData has two
  // callers — the params effect and the retry button — and a retry's late
  // answer must not overwrite a newer period's rows (self-review, 20/10/2026).
  const reqIdRef = useRef(0);
  const fetchData = useCallback(() => {
    const reqId = ++reqIdRef.current;
    const current = () => reqId === reqIdRef.current;
    setLoading(true);
    setLoadError(false);
    analysisApi.getTopProducts(params)
      .then(r => { if (current()) setRows(Array.isArray(r.data) ? r.data : []); })
      // Never fall through to the empty state on an error: "no purchases" is
      // a claim about the restaurant's money, a failed request is not.
      .catch(() => { if (current()) { setLoadError(true); setRows([]); } })
      .finally(() => { if (current()) setLoading(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const period = periodLabel(params?.date_from, params?.date_to);

  const goToReport = () => {
    // /analysis's preset rides as `?period=<key>`, and the supplier filter
    // travels too, so the report opens on the same money as this card.
    const preset = activePresetKey(params?.date_from, params?.date_to, PRESETS_ANALYTIC);
    const qs = withPeriodParams(new URLSearchParams(), preset, params?.date_from, params?.date_to);
    // `!== undefined`, not truthiness: an EMPTY list means the page's filter
    // matched no supplier, and must reach the report as such (Codex, PR #309).
    if (params?.supplier_ids !== undefined) qs.set('supplier_ids', params.supplier_ids);
    const s = qs.toString();
    navigate(`${TOP_PRODUCTS_REPORT_PATH}${s ? `?${s}` : ''}`);
  };

  const header = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
      <div>
        <h3 style={{ margin: 0 }}>
          <button
            type="button"
            className="card-title-link"
            onClick={goToReport}
            onMouseEnter={() => setTitleHover(true)}
            onMouseLeave={() => setTitleHover(false)}
            aria-label="צפייה בדוח המלא של המוצרים המובילים"
            style={{
              font: 'inherit', fontSize: '1rem', fontWeight: 700, color: 'var(--ink-muted)',
              background: 'none', border: 'none', padding: 0,
              textDecoration: titleHover ? 'underline' : 'none',
            }}
          >10 המוצרים המובילים</button>
        </h3>
        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-subtle)', marginTop: 3 }}>ללא מע״מ</div>
      </div>
      <div className="page-menu" style={{ position: 'relative' }}>
        <button
          className="page-menu-trigger"
          aria-label="פעולות נוספות"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(o => !o)}
        >⋮</button>
        {menuOpen && (
          <>
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
            <div className="page-menu-panel" style={{ minWidth: 290 }}>
              <button
                className="page-menu-item"
                onClick={() => { setMenuOpen(false); goToReport(); }}
                title="כל המוצרים בתקופה, לפי סכום"
              >
                <Icon name="list" size={15} /> צפייה בדוח המלא
              </button>
              <TopProductsWhyItMatters />
              <TopProductsHowCalculated />
            </div>
          </>
        )}
      </div>
    </div>
  );

  // The product cell is the same on both: the name, its supplier under it.
  // The price cell differs — the % under the price on a phone, beside it on
  // desktop in a fixed slot, so a wide chip (+11.1%) never shifts its price
  // out of line with the rows above and below.
  const cols = phone ? 'tp-cols-phone' : 'tp-cols-desk';
  const table = (
    <div className={phone ? 'tp-table' : 'tp-table tp-table-desk'} role="table" aria-label="10 המוצרים המובילים">
      <div className={`tp-head ${cols}`} role="row">
        <span role="columnheader">מוצר · ספק</span>
        {/* On desktop, offset by the chip slot so the label sits over the prices. */}
        <span role="columnheader" className={phone ? 'tp-num' : 'tp-num tp-head-price'}>מחיר נוכחי</span>
        <span role="columnheader" className="tp-num">סה״כ רכש</span>
      </div>
      {rows.map(r => (
        <div key={topProductKey(r)} className={`tp-row ${cols}`} role="row">
          <div role="cell" className="tp-cell-text">
            <div className="tp-name">{r.name}</div>
            <div className="tp-sup">{r.supplier_name}</div>
          </div>
          {phone ? (
            <div role="cell" className="tp-num">
              <div className="tp-price">{price(r)}</div>
              <div className="tp-chip-line"><RowChangeChip row={r} /></div>
            </div>
          ) : (
            <div role="cell" className="tp-num tp-price-inline">
              <span className="tp-price">{price(r)}</span>
              <span className="tp-chip-slot"><RowChangeChip row={r} /></span>
            </div>
          )}
          <div role="cell" className="tp-num tp-total">{formatCurrencyWhole(r.spend)}</div>
        </div>
      ))}
    </div>
  );

  let body;
  if (loading) {
    body = <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>;
  } else if (loadError) {
    body = <InlineLoadError message="לא ניתן לטעון את המוצרים המובילים" onRetry={fetchData} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState compact message={filterActive ? `לא נמצאו מוצרים ב${period} לפי הסינון שנבחר` : `אין רכישות של מוצרים ב${period}`}>
        <p className="empty-subline">אפשר לבחור תקופה אחרת.</p>
      </EmptyState>
    );
  } else {
    body = table;
  }

  return (
    <div className="card top-products">
      {header}
      {body}
      <div className="top-products-foot">
        <button type="button" className="btn btn-sm btn-secondary" onClick={goToReport}>
          לצפייה <Icon name="chevron" size={13} style={{ transform: 'rotate(90deg)' }} />
        </button>
      </div>
    </div>
  );
}
